// src/app/pages/trend/trend.ts
import { Component, ChangeDetectionStrategy, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { AccordionModule } from 'primeng/accordion';

import { AnalysisStore } from '../../core/shared/analysis.store';
import { InterventionStore } from '../../core/shared/intervention.store';

type MetricValue =
  | 'controlled' | 'treated_not_controlled' | 'diagnosed' | 'undiagnosed'
  | 'prevalence' | 'screened' | 'aware_of_screening'
  | 'cvd_events' | 'cvd_patients' | 'cvd_deaths';
type GroupValue = 'total' | 'male' | 'female' | 'average';
type SeriesPoint = { x: number; y: number };


interface CvdRow {
  year: number;
  diagnosed_total: number; diagnosed_male: number; diagnosed_female: number;
  undiagnosed_total: number; undiagnosed_male: number; undiagnosed_female: number;
  treated_total: number; treated_male: number; treated_female: number;
  controlled_total?: number; controlled_male: number; controlled_female: number;

  // NEW optional fields (if present in rows)
  screened_total?: number; screened_male?: number; screened_female?: number;
  aware_of_screening_total?: number; aware_of_screening_male?: number; aware_of_screening_female?: number;
  cvd_events_total?: number; cvd_events_male?: number; cvd_events_female?: number;
  cvd_patients_total?: number; cvd_patients_male?: number; cvd_patients_female?: number;
  cvd_deaths_total?: number; cvd_deaths_male?: number; cvd_deaths_female?: number;
}

@Component({
  selector: 'app-trend',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    ChartModule, SelectModule,
    ButtonModule, TooltipModule, CardModule, AccordionModule
  ],
  templateUrl: './trend.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TrendComponent implements OnInit {
  mode: 'line' | 'bar' = 'line';
  metric: MetricValue = 'diagnosed';
  sex: GroupValue = 'male';

  private analysisStore = inject(AnalysisStore);
  private interventionStore = inject(InterventionStore);

  private baseRows: CvdRow[] = [];
  private runRowsMap: Record<string, CvdRow[]> = {};
  private runTitles: Record<string, string> = {};

  private readonly displayNameMap: Record<string, string> = {
    'Awareness raising': 'Improve Health Literacy ',
    'Screening corner': 'PHC Screening Corner',
    'Active search': 'Mobile RF Testing',
    'Diagnostic capacity UBS': 'Increased Diagnostic Capacity',
    'Treatment capacity UBS': 'Increased Treatment Capacity',
    'Caring for all protocol': 'Comprehensive Care Protocol',
    'Remote training': 'Remote Medical Education',
    'Risk stratification tool': 'Risk Stratification Tool',
    'PAP card': 'Patient Self-Management',
    'Adherence combo': 'Treatment Adherence Tools',
    'Governance model': 'Governance Model',
    'Management panel': 'Management Panel'
  };

  simulationOptions: Array<{ label: string; value: string }> = [];
  selectedSimulationId: string | null = null;

  trendData: any = { datasets: [] };
  trendOptions: any = {};

  metricOptions = [
    { label: 'Controlled', value: 'controlled' },
    { label: 'Treated not controlled', value: 'treated_not_controlled' },
    { label: 'Diagnosed', value: 'diagnosed' },
    { label: 'Undiagnosed', value: 'undiagnosed' },
    { label: 'Prevalence', value: 'prevalence' },
    { label: 'Screened', value: 'screened' },
    { label: 'Aware of screening', value: 'aware_of_screening' },
    { label: 'CVD events', value: 'cvd_events' },
    { label: 'CVD patients', value: 'cvd_patients' },
    { label: 'CVD deaths', value: 'cvd_deaths' }
  ];
  sexOptions = [
    { label: 'Total', value: 'total' },
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Average', value: 'average' }
  ];

  // Accordion indices (only one open at a time)
  activeParamIndex = 0;
  activeCostIndex = 0;

  // Extracted groups
  paramGroups: Array<{ name: string; pairs: Array<{ key: string; val: string }> }> = [];
  costGroups: Array<{ name: string; pairs: Array<{ key: string; val: string }> }> = [];

  // Insight Summary (HTML)
  insightSummaryHtml = '';

  ngOnInit(): void {
    this.analysisStore.runsList$.subscribe(runs => {
      if (!runs?.length) {
        this.baseRows = [];
        this.runRowsMap = {};
        this.runTitles = {};
        this.simulationOptions = [];
        this.selectedSimulationId = null;
        this.buildChart();
        this.updateSummary();
        return;
      }

      const baseRun = runs[0];
      this.baseRows = this.extractRows(baseRun);

      const others = runs.slice(1);
      this.simulationOptions = others.map(r => ({ label: r.title ?? `Simulation ${r.id ?? ''}`, value: r.id }));

      this.runRowsMap = {};
      this.runTitles = {};
      others.forEach(r => {
        this.runRowsMap[r.id] = this.extractRows(r);
        this.runTitles[r.id] = r.title ?? `Simulation ${r.id ?? ''}`;
      });

      if (!this.selectedSimulationId && others.length) {
        this.selectedSimulationId = others[others.length - 1].id;
      } else {
        const validIds = new Set(this.simulationOptions.map(o => o.value));
        if (this.selectedSimulationId && !validIds.has(this.selectedSimulationId)) {
          this.selectedSimulationId = others.length ? others[others.length - 1].id : null;
        }
      }

      this.refreshSelectedParams();
      this.buildChart();
      this.updateSummary();
    });

    Promise.resolve().then(() => {
      this.buildChart();
      this.updateSummary();
    });
  }

  toggle(type: 'line' | 'bar') {
    if (this.mode !== type) {
      this.mode = type;
      this.buildChart();
    }
  }

  onSelectChange() {
    this.buildChart();
    this.updateSummary();
  }

  onSimulationSelectionChange(id: string) {
    this.selectedSimulationId = id || null;
    this.refreshSelectedParams();
    this.buildChart();
    this.updateSummary();
  }

  private refreshSelectedParams() {
    this.paramGroups = [];
    this.costGroups = [];
    if (!this.selectedSimulationId) return;

    const snap: any = (this.interventionStore as any)?.getSnapshot?.(String(this.selectedSimulationId));
    const params = snap?.parameters || {};
    const cm = snap?.costmetrics || {};
    const selectedOptions = snap?.selectedOptions || {};

    const norm = (s: string) => String(s || '').toLowerCase().replace(/\s+/g, '');
    const selectedSet = new Set<string>();
    Object.keys(selectedOptions).forEach(cat => {
      (selectedOptions[cat] || []).forEach((name: string) => selectedSet.add(norm(name)));
    });

    Object.keys(params).forEach(group => {
      const v = params[group];
      if (v && typeof v === 'object' && selectedSet.has(norm(group))) {
        const pairs = Object.keys(v).map(k => ({ key: k, val: String(v[k]) }));
        const displayName = this.displayNameMap[group] ?? group;
        this.paramGroups.push({ name: displayName, pairs });
      }
    });

    Object.keys(cm).forEach(group => {
      if (!selectedSet.has(norm(group))) return;
      const kv = cm[group] || {};
      const pairs = Object.keys(kv).map(k => ({ key: k, val: String(kv[k]) }));
      this.costGroups.push({ name: group, pairs });
    });
  }

  // Replace the entire extractRows function with this version
  private extractRows(run: any): CvdRow[] {
    const srcRows = (run?.rows ?? []) as any[];

    // Safe numeric parser: handles "1,234" → 1234, null/undefined/'' → 0
    const num = (v: any): number => {
      if (v === undefined || v === null || v === '') return 0;
      if (typeof v === 'string') {
        const cleaned = v.replace(/[, ]/g, '');
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : 0;
      }
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    return (srcRows || []).map((r: any) => ({
      year: Number(r.year ?? 0),

      diagnosed_total: num(r.diagnosed_total ?? 0),
      diagnosed_male: num(r.diagnosed_male ?? 0),
      diagnosed_female: num(r.diagnosed_female ?? 0),

      undiagnosed_total: num(r.undiagnosed_total ?? 0),
      undiagnosed_male: num(r.undiagnosed_male ?? 0),
      undiagnosed_female: num(r.undiagnosed_female ?? 0),

      treated_total: num(r.treated_total ?? 0),
      treated_male: num(r.treated_male ?? 0),
      treated_female: num(r.treated_female ?? 0),

      controlled_total: num((r as any).controlled_total ?? (num(r.controlled_male ?? 0) + num(r.controlled_female ?? 0))),
      controlled_male: num(r.controlled_male ?? 0),
      controlled_female: num(r.controlled_female ?? 0),

      // Screened: support 'screened_total', plain 'screened', and 'screened_practice_*'
      screened_total: num(
        r.screened_total ??
        ((num(r.screened_practice_male) + num(r.screened_practice_female)) || num(r.screened))
      ),
      screened_male: num(r.screened_male ?? r.screened_practice_male ?? 0),
      screened_female: num(r.screened_female ?? r.screened_practice_female ?? 0),

      // Aware of screening: support '*_total', plain, and '*_knowledge_*' variants
      aware_of_screening_total: num(
        r.aware_of_screening_total ??
        ((num(r.aware_of_screening_knowledge_male) + num(r.aware_of_screening_knowledge_female)) || num(r.aware_of_screening))
      ),
      aware_of_screening_male: num(r.aware_of_screening_male ?? r.aware_of_screening_knowledge_male ?? 0),
      aware_of_screening_female: num(r.aware_of_screening_female ?? r.aware_of_screening_knowledge_female ?? 0),

      // CVD events: tolerate case and variants
      cvd_events_total: num(
        (r.cvd_events_total ??
          r.cvd_events ??
          r.CVD_Events_total ??
          r.CVD_Events ??
          0)
      ),
      cvd_events_male: num(r.cvd_events_male ?? r.CVD_Events_Male ?? 0),
      cvd_events_female: num(r.cvd_events_female ?? r.CVD_Events_Female ?? 0),

      // CVD patients: explicit total variants; if total missing, fallback to male+female
      cvd_patients_total: (() => {
        const rawTotal =
          (r as any).cvd_patients_total ??
          (r as any).cvd_patients ??
          (r as any).CVD_Patients_total ??
          (r as any).CVD_Patients;

        if (rawTotal !== undefined && rawTotal !== null && rawTotal !== '') {
          // Use explicit total as-is (even if 0), parsed safely
          return num(rawTotal);
        }
        // Explicit total missing → fallback to male + female
        const male = num((r as any).cvd_patients_male ?? (r as any).CVD_Patients_Male);
        const female = num((r as any).cvd_patients_female ?? (r as any).CVD_Patients_Female);
        return male + female;
      })(),
      cvd_patients_male: num((r as any).cvd_patients_male ?? (r as any).CVD_Patients_Male ?? 0),
      cvd_patients_female: num((r as any).cvd_patients_female ?? (r as any).CVD_Patients_Female ?? 0),

      // CVD deaths: accept both 'cvd_deaths_*' and 'cvd_death_*' variants
      cvd_deaths_total: num(
        (r.cvd_deaths_total ??
          r.cvd_deaths ??
          r.cvd_death_total ??
          r.cvd_death ??
          0)
      ),
      cvd_deaths_male: num(r.cvd_deaths_male ?? r.cvd_death_male ?? 0),
      cvd_deaths_female: num(r.cvd_deaths_female ?? r.cvd_death_female ?? 0)
    }));
  }

  // Replace the entire pickValue function with this version
  private pickValue(r: CvdRow & Record<string, any>, metric: MetricValue, group: GroupValue): number {
    const rr = r as any;

    const byGroup = (base: string): number => {
      // total: use explicit total or plain key; do not synthesize except where extractRows already normalized (e.g., cvd_patients_total)
      const total = Number(rr[`${base}_total`] ?? rr[base] ?? 0);
      const male = Number(rr[`${base}_male`] ?? 0);
      const female = Number(rr[`${base}_female`] ?? 0);

      if (group === 'male') return male;
      if (group === 'female') return female;
      if (group === 'total') return total;
      // average = (male + female)/2
      return (male + female) / 2;
    };

    switch (metric) {
      case 'diagnosed': return byGroup('diagnosed');
      case 'undiagnosed': return byGroup('undiagnosed');

      case 'treated_not_controlled': {
        const treated = byGroup('treated');
        const controlled = byGroup('controlled');
        return Math.max(0, treated - controlled);
      }

      case 'controlled': return byGroup('controlled');

      case 'prevalence': {
        const d = byGroup('diagnosed');
        const u = byGroup('undiagnosed');
        return Math.max(0, d + u);
      }

      // SCREENED: try direct fields, then fuzzy ('screened' or 'screening')
      case 'screened': {
        const direct = byGroup('screened');
        if (direct) return direct;
        const v1 = this.findMetricValue(rr, ['screened'], group);
        const v2 = this.findMetricValue(rr, ['screening'], group);
        return Math.max(v1, v2);
      }

      // AWARE OF SCREENING: try direct, then fuzzy ('aware'+'screen', 'awareness'+'screen')
      case 'aware_of_screening': {
        const direct = byGroup('aware_of_screening');
        if (direct) return direct;
        const v1 = this.findMetricValue(rr, ['aware', 'screen'], group);
        const v2 = this.findMetricValue(rr, ['awareness', 'screen'], group);
        return Math.max(v1, v2);
      }

      // CVD EVENTS/PATIENTS/DEATHS: try direct, then fuzzy
      case 'cvd_events': {
        const direct = byGroup('cvd_events');
        if (direct) return direct;
        return this.findMetricValue(rr, ['cvd', 'event'], group);
      }
      case 'cvd_patients': {
        const direct = byGroup('cvd_patients');
        if (direct) return direct;
        return this.findMetricValue(rr, ['cvd', 'patient'], group);
      }
      case 'cvd_deaths': {
        const direct = byGroup('cvd_deaths');
        if (direct) return direct;
        const v1 = this.findMetricValue(rr, ['cvd', 'death'], group);
        const v2 = this.findMetricValue(rr, ['death', 'cvd'], group);
        const v3 = this.findMetricValue(rr, ['deaths', 'cvd'], group);
        return Math.max(v1, v2, v3);
      }
    }
  }

  private toSeries(rows: CvdRow[], metric: MetricValue, group: GroupValue): SeriesPoint[] {
    return rows.map(r => ({ x: r.year, y: Math.max(0, Math.round(this.pickValue(r, metric, group))) }));
  }

  private buildChart() {
    const datasets: any[] = [];

    // Baseline dataset (mild pink)
    const baseSeries = this.toSeries(this.baseRows, this.metric, this.sex);
    if (this.mode === 'line') {
      datasets.push({
        label: 'Simulation 1 (Baseline)',
        data: baseSeries,
        borderColor: '#f9a8d4',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.25
      });
    } else {
      datasets.push({
        label: 'Simulation 1 (Baseline)',
        data: baseSeries,
        backgroundColor: 'rgba(249,168,212,0.55)',
        borderColor: '#f9a8d4',
        borderWidth: 1
      });
    }

    // Selected scenario (dark pink)
    if (this.selectedSimulationId) {
      const rows = this.runRowsMap[this.selectedSimulationId] ?? [];
      const series = this.toSeries(rows, this.metric, this.sex);
      const color = '#be185d';

      if (this.mode === 'line') {
        datasets.push({
          label: this.runTitles[this.selectedSimulationId] ?? 'Selected Simulation',
          data: series,
          borderColor: color,
          backgroundColor: 'transparent',
          borderWidth: 3,
          pointRadius: 2,
          tension: 0.25
        });
      } else {
        datasets.push({
          label: this.runTitles[this.selectedSimulationId] ?? 'Selected Simulation',
          data: series,
          backgroundColor: 'rgba(190,24,93,0.75)',
          borderColor: color,
          borderWidth: 1
        });
      }
    }

    this.trendData = { datasets };
    this.trendOptions = this.makeOptions(datasets);
  }

  private makeOptions(datasets?: any) {
    const allX = (datasets || [])
      .flatMap((ds: any) => (ds.data || []).map((p: any) => p?.x))
      .filter((x: any) => typeof x === 'number') as number[];
    const minYear = allX.length ? Math.min(...allX) : 2011;
    const maxYear = allX.length ? Math.max(...allX) : 2045;

    return {
      responsive: true,
      maintainAspectRatio: true,
      parsing: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: { color: 'var(--text-color, #334155)', usePointStyle: true, boxWidth: 12 }
        },
        tooltip: {
          mode: this.mode === 'bar' ? 'index' : 'nearest',
          intersect: false,
          callbacks: {
            title: (items: any[]) => `Year: ${String(items[0]?.raw?.x)}`,
            label: (ctx: any) =>
              `${ctx.dataset.label}: ${new Intl.NumberFormat('en-US', { notation: 'compact' }).format(ctx.raw?.y ?? 0)}`
          }
        }
      },
      scales: {
        x: {
          type: 'linear',
          min: minYear,
          max: maxYear,
          ticks: {
            maxTicksLimit: 10,
            color: 'var(--text-secondary-color, #64748B)',
            callback: (v: number) => String(Math.round(v)),
            font: { size: 10 }
          },
          grid: {
            display: false,
            color: 'rgba(100,116,139,0.08)'
          },
          title: { display: true, text: 'Year', color: 'var(--text-secondary-color, #64748B)' }
        },
        y: {
          beginAtZero: true,
          ticks: {
            maxTicksLimit: 5,
            color: 'var(--text-secondary-color, #64748B)',
            callback: (v: number) => new Intl.NumberFormat('en-US', { notation: 'compact' }).format(v),
            font: { size: 10 }
          },
          grid: { color: 'rgba(100,116,139,0.08)' },
          title: { display: true, text: 'No. of people', color: 'var(--text-secondary-color, #64748B)' }
        }
      }
    };
  }

  private updateSummary() {
    const city = 'Sao Paulo';
    const horizonYears = (this.baseRows.length ? (this.baseRows[this.baseRows.length - 1].year - this.baseRows[0].year) : 30) || 30;
    const metricLabel = (this.metricOptions.find(m => m.value === this.metric)?.label) || 'Metric';

    const baseStart = this.baseRows.length ? Math.round(this.pickValue(this.baseRows[0], this.metric, this.sex)) : 0;
    const baseEnd = this.baseRows.length ? Math.round(this.pickValue(this.baseRows[this.baseRows.length - 1], this.metric, this.sex)) : 0;

    let selStart = 0, selEnd = 0;
    if (this.selectedSimulationId) {
      const rows = this.runRowsMap[this.selectedSimulationId] || [];
      selStart = rows.length ? Math.round(this.pickValue(rows[0], this.metric, this.sex)) : 0;
      selEnd = rows.length ? Math.round(this.pickValue(rows[rows.length - 1], this.metric, this.sex)) : 0;
    }

    const h = (v: number) => `<span style="background:#e0f2fe;color:#0c4a6e;padding:0 4px;border-radius:4px;">${this.nf(v)}</span>`;
    const baseStartH = h(baseStart);
    const baseEndH = h(baseEnd);
    const selStartH = h(selStart);
    const selEndH = h(selEnd);

    this.insightSummaryHtml =
      `In the base scenario, ${metricLabel.toLowerCase()} changes from ${baseStartH} to ${baseEndH} ` +
      `over ${horizonYears} years for ${city}’s hypertensive population. ` +
      (this.selectedSimulationId
        ? `With CARDIO4Cities, ${metricLabel.toLowerCase()} changes from ${selStartH} to ${selEndH}, ` +
        `delivering better heart health. `
        : '') +
      `This underscores the impact of strategic cardiovascular action versus passive system evolution alone.`;
  }

  nf(n: number): string {
    return new Intl.NumberFormat('en-US').format(n);
  }
  showInterventions = true;
  showCostmetrics = false;

  toggleSection(which: 'params' | 'cost') {
    if (which === 'params') {
      this.showInterventions = true;
      this.showCostmetrics = false;
    } else {
      this.showInterventions = false;
      this.showCostmetrics = true;
    }
  }
  private findValue(rr: Record<string, any>, tokens: string[], group: GroupValue): number {
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const want = tokens.map(norm);
    const suffix =
      group === 'male' ? 'male' :
        group === 'female' ? 'female' :
          'total';

    // First pass: exact group-specific keys (e.g., screened_total/male/female)
    const directKeys = [
      `${tokens[0]}_${suffix}`,                  // screened_total
      `${tokens.join('_')}_${suffix}`,          // aware_of_screening_total
      `${tokens[0]}${suffix}`,                  // screenedtotal
      `${tokens.join('')}${suffix}`             // awareofscreeningtotal
    ].map(norm);

    for (const k of Object.keys(rr)) {
      const nk = norm(k);
      if (directKeys.some(d => nk.includes(d))) {
        const v = Number(rr[k]);
        if (Number.isFinite(v)) return v;
      }
    }

    // Second pass: fuzzy match — key must contain all tokens and the suffix (group)
    for (const k of Object.keys(rr)) {
      const nk = norm(k);
      const hasAllTokens = want.every(t => nk.includes(t));
      const hasSuffix = nk.includes(norm(suffix));
      if (hasAllTokens && hasSuffix) {
        const v = Number(rr[k]);
        if (Number.isFinite(v)) return v;
      }
    }

    // Fallback for 'total': accept token-only if group-specific missing
    if (group === 'total') {
      for (const k of Object.keys(rr)) {
        const nk = norm(k);
        const hasAllTokens = want.every(t => nk.includes(t));
        if (hasAllTokens) {
          const v = Number(rr[k]);
          if (Number.isFinite(v)) return v;
        }
      }
    }

    return 0;
  }

  private findMetricValue(rr: Record<string, any>, tokens: string[], group: GroupValue): number {
    const norm = (s: string) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const want = tokens.map(norm);
    const suffix = group === 'male' ? 'male' : group === 'female' ? 'female' : 'total';
    const hasAllTokens = (key: string) => want.every(t => key.includes(t));

    // Prefer exact group-suffixed keys (e.g., aware_of_screening_total/male/female)
    for (const k of Object.keys(rr)) {
      const nk = norm(k);
      if (hasAllTokens(nk) && nk.includes(norm(suffix))) {
        const v = Number(rr[k]);
        if (Number.isFinite(v)) return v;
      }
    }

    // IMPORTANT: No synthetic total from male+female — per your rule.

    // Last resort: if backend uses a plain token-only key (treated as “total” by convention)
    for (const k of Object.keys(rr)) {
      const nk = norm(k);
      if (hasAllTokens(nk)) {
        const v = Number(rr[k]);
        if (Number.isFinite(v)) return v;
      }
    }

    return 0;
  }
}